import type { ScoreValidationRequest, ScoreValidationResponse } from './shared/scoreValidation';
import { SCORE_VALIDATION_ROUTE } from './shared/scoreValidation';

const PLAYFUN_SDK_URL = 'https://sdk.play.fun';
const PLAYFUN_SDK_LOAD_TIMEOUT_MS = 15_000;
const PLAYFUN_SDK_LOAD_RETRIES = 3;
const OGP_LOGIN_TIMEOUT_MS = 5_000;
const OGP_SESSION_TOKEN_BLOCKING_MS = 5_000;
const OGP_SESSION_TOKEN_BACKGROUND_MS = 20_000;

const resolvedPlayFunSdkUrl = (() => {
  const raw = (process.env.PLAYFUN_SDK_URL || '').trim();
  return raw || PLAYFUN_SDK_URL;
})();

const defaultOGPBaseUrl = '';
const defaultScoreApiBaseUrl = (process.env.ROCKETSHEEP_SCORE_API || '').trim() || undefined;
const defaultUsePointsWidget = (() => {
  const raw = (process.env.OGP_USE_POINTS_WIDGET || '').trim().toLowerCase();
  if (!raw) return true;
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();
const disableClientSDK = (() => {
  const raw = (process.env.OGP_DISABLE_CLIENT_SDK || '').trim().toLowerCase();
  if (!raw) return false;
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();

const OGP_PLAYER_ID_HANDSHAKE_INTERVAL_MS = 1_000;
const OGP_PLAYER_ID_POLL_INTERVAL_MS = 100;
const OGP_SESSION_TOKEN_POLL_INTERVAL_MS = 300;
const OGP_SESSION_TOKEN_MAX_RETRIES_ONLINE = 3;

type PlayFunSDKConstructor = new (args: any) => any;

type PlayFunSDKLoadResult = {
  ctor: PlayFunSDKConstructor;
};

type OGPConfig = {
  apiKey?: string;
  gameId?: string;
  baseUrl?: string;
  scoreApiBaseUrl?: string;
};

type OGPSession = {
  sdk: any | null;
  playerId?: string;
  gameId?: string;
  sessionToken?: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, timeoutError: string): Promise<T> => {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return await promise;
  }
  let timeoutId: number | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error(timeoutError)), timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
};

const normalizeOptionalString = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const value = raw.trim();
  if (!value) return undefined;
  const lowered = value.toLowerCase();
  if (lowered === 'undefined' || lowered === 'null') return undefined;
  return value;
};

const pickFirstDefinedString = (...values: Array<unknown>): string | undefined => {
  for (let i = 0; i < values.length; i++) {
    const value = normalizeOptionalString(values[i]);
    if (value) return value;
  }
  return undefined;
};

const hasParentFrame = () => {
  if (typeof window === 'undefined') return false;
  return window.parent !== window;
};

const isPlayFunHostname = (value: string): boolean => {
  const host = normalizeOptionalString(value)?.toLowerCase();
  if (!host) return false;
  return host === 'play.fun' || host.endsWith('.play.fun');
};

const getHostnameFromUrl = (value: string): string | undefined => {
  const normalized = normalizeOptionalString(value);
  if (!normalized) return undefined;
  try {
    return new URL(normalized).hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

export const isEmbeddedInPlayFunPlatform = (): boolean => {
  if (typeof window === 'undefined' || !hasParentFrame()) {
    return false;
  }

  const embeddedFlag = pickFirstDefinedString(
    (window as any).__APOLLO_CONFIG__?.playfunEmbedded,
    (window as any).__APOLLO_CONFIG__?.ogpEmbedded,
    readSearchParam('playfunEmbedded'),
    readSearchParam('ogpEmbedded')
  );
  if (embeddedFlag) {
    const normalized = embeddedFlag.toLowerCase();
    if (normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on') {
      return true;
    }
  }

  const referrerHost = getHostnameFromUrl(typeof document !== 'undefined' ? document.referrer : '');
  if (referrerHost && isPlayFunHostname(referrerHost)) {
    return true;
  }

  const ancestorOrigins = (window.location as any)?.ancestorOrigins;
  if (ancestorOrigins && typeof ancestorOrigins.length === 'number') {
    for (let i = 0; i < ancestorOrigins.length; i++) {
      const ancestorHost = getHostnameFromUrl(ancestorOrigins[i]);
      if (ancestorHost && isPlayFunHostname(ancestorHost)) {
        return true;
      }
    }
  }

  try {
    const topHost = window.top?.location?.hostname?.toLowerCase();
    if (topHost && isPlayFunHostname(topHost)) {
      return true;
    }
  } catch {
    // Cross-origin top frame, ignore.
  }

  return false;
};

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
};

const readMeta = (name: string) => {
  if (typeof document === 'undefined') return '';
  return normalizeOptionalString(document.querySelector(`meta[name="${name}"]`)?.getAttribute('content')) || '';
};

const readSearchParam = (key: string): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  return normalizeOptionalString(params.get(key));
};

const getConfig = (): OGPConfig => {
  if (typeof window === 'undefined') {
    return {
      scoreApiBaseUrl: defaultScoreApiBaseUrl,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const scoreApiBaseUrl = pickFirstDefinedString(
    (window as any).__APOLLO_CONFIG__?.scoreApiBaseUrl,
    params.get('scoreApi'),
    readMeta('rocketsheep-score-api'),
    defaultScoreApiBaseUrl
  );

  return {
    apiKey: readMeta('x-ogp-key') || undefined,
    gameId: pickFirstDefinedString(readMeta('x-ogp-game-id'), params.get('gameId')),
    baseUrl: pickFirstDefinedString(
      (window as any).__APOLLO_CONFIG__?.ogpBaseUrl,
      params.get('ogpBaseUrl'),
      params.get('baseUrl'),
      readMeta('x-ogp-base-url'),
      defaultOGPBaseUrl
    ),
    scoreApiBaseUrl: scoreApiBaseUrl || undefined,
  };
};

export const getOGPConfig = (): OGPConfig => getConfig();

const hasCompleteSDKConfig = (cfg?: OGPConfig): boolean => {
  const config = cfg || getConfig();
  return !!(config.apiKey && config.gameId);
};

const getPlayerIdFromClientContext = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return pickFirstDefinedString(
    (window as any).__APOLLO_CONFIG__?.ogpPlayerId,
    readSearchParam('playerId'),
    readSearchParam('ogpPlayerId'),
    readSearchParam('ogp_player_id')
  );
};

const getSessionTokenFromClientContext = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return pickFirstDefinedString(
    (window as any).__APOLLO_CONFIG__?.sessionToken,
    (window as any).__APOLLO_CONFIG__?.ogpSessionToken,
    readSearchParam('sessionToken'),
    readSearchParam('ogpSessionToken'),
    readSearchParam('ogp_session_token')
  );
};

const isAllowedOGPMessageOrigin = (origin: string): boolean => {
  try {
    const hostname = new URL(origin).hostname.toLowerCase();
    return hostname === 'play.fun' || hostname.endsWith('.play.fun');
  } catch {
    return false;
  }
};

const extractPlayerIdFromMessage = (data: any): string | undefined => {
  if (!data) return undefined;
  return pickFirstDefinedString(data.data?.playerId, data.playerId);
};

const extractSessionTokenFromMessage = (data: any): string | undefined => {
  if (!data) return undefined;
  return pickFirstDefinedString(data.data?.sessionToken, data.data?.token, data.sessionToken, data.token);
};

let didAttachOGPMessageListener = false;
let cachedMessagePlayerId: string | undefined;
let cachedMessageSessionToken: string | undefined;

const logFetchedIdentifiers = (source: string, values?: { playerId?: string; sessionToken?: string }) => {
  const playerId = values?.playerId || cachedMessagePlayerId || getPlayerIdFromClientContext();
  const sessionToken = values?.sessionToken || cachedMessageSessionToken || getSessionTokenFromClientContext();
  console.error('[OGP debug] fetched identifiers', {
    source,
    playerId,
    sessionToken,
  });
};

const requestEmbedCredentials = (reason: string) => {
  if (typeof window === 'undefined' || !hasParentFrame()) {
    return;
  }

  const now = Date.now();
  const messages = [
    { type: 'REQUEST_PLAYER_ID', source: 'apollo-js', reason, ts: now },
    { action: 'REQUEST_PLAYER_ID', source: 'apollo-js', reason, ts: now },
    { type: 'PLAYFUN_REQUEST_PLAYER_ID', action: 'PLAYFUN_REQUEST_PLAYER_ID', source: 'apollo-js', reason, ts: now },
    { type: 'REQUEST_SESSION_TOKEN', action: 'REQUEST_SESSION_TOKEN', source: 'apollo-js', reason, ts: now },
    { type: 'PLAYFUN_REQUEST_SESSION_TOKEN', action: 'PLAYFUN_REQUEST_SESSION_TOKEN', source: 'apollo-js', reason, ts: now },
  ];

  for (let i = 0; i < messages.length; i++) {
    try {
      window.parent.postMessage(messages[i], '*');
    } catch {
      // no-op
    }
  }
};

const handleOGPWindowMessage = (event: MessageEvent) => {
  const playerId = extractPlayerIdFromMessage(event.data);
  const sessionToken = extractSessionTokenFromMessage(event.data);
  if (!playerId && !sessionToken) {
    return;
  }

  const allowedOrigin = isAllowedOGPMessageOrigin(event.origin);
  const fromParent = hasParentFrame() && event.source === window.parent;
  if (!allowedOrigin && !fromParent) {
    return;
  }

  if (!allowedOrigin && fromParent) {
    console.warn('[OGP debug] accepting parent-frame credentials from non-whitelisted origin', {
      origin: event.origin,
      hasPlayerId: !!playerId,
      hasSessionToken: !!sessionToken,
    });
  }

  if (playerId) cachedMessagePlayerId = playerId;
  if (sessionToken) cachedMessageSessionToken = sessionToken;

  logFetchedIdentifiers('postmessage', { playerId, sessionToken });
};

const ensureOGPMessageListener = () => {
  if (didAttachOGPMessageListener || typeof window === 'undefined') {
    return;
  }
  window.addEventListener('message', handleOGPWindowMessage as any);
  didAttachOGPMessageListener = true;
  requestEmbedCredentials('listener_attached');
};

if (typeof window !== 'undefined') {
  ensureOGPMessageListener();
}

const getPlayerIdFromSDK = (sdk: any): string | undefined => {
  if (!sdk) return undefined;
  return pickFirstDefinedString(sdk.playerId, sdk.state?.playerId, sdk.state?.auth?.playerId);
};

const getSessionTokenFromSDK = (sdk: any): string | undefined => {
  if (!sdk) return undefined;

  try {
    if (typeof sdk.getSessionToken === 'function') {
      const token = sdk.getSessionToken();
      const normalized = normalizeOptionalString(token);
      if (normalized) return normalized;
    }
  } catch {
    // Best effort fallback below.
  }

  return pickFirstDefinedString(
    sdk.state?.session?.token,
    sdk.state?.sessionToken,
    sdk.sessionToken,
    sdk._sessionToken
  );
};

const getImmediatePlayerId = (sdk?: any | null): string | undefined => {
  return pickFirstDefinedString(
    getPlayerIdFromClientContext(),
    cachedMessagePlayerId,
    getPlayerIdFromSDK(sdk),
    getPlayerIdFromSDK(ogpState.sdk)
  );
};

const getImmediateSessionToken = (sdk?: any | null): string | undefined => {
  return pickFirstDefinedString(
    getSessionTokenFromClientContext(),
    cachedMessageSessionToken,
    getSessionTokenFromSDK(sdk),
    getSessionTokenFromSDK(ogpState.sdk)
  );
};

const getGlobalPlayFunSDK = (): { ctor: PlayFunSDKConstructor } | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const w = window as any;
  const candidates: Array<{ key: string; ctor: PlayFunSDKConstructor | undefined }> = [
    { key: 'OpenGameSDK', ctor: w.OpenGameSDK },
    { key: 'PlayFunSDK', ctor: w.PlayFunSDK },
    { key: 'PlayDotFunSDK', ctor: w.PlayDotFunSDK },
    { key: 'playFunSDK', ctor: w.playFunSDK },
  ];

  for (let i = 0; i < candidates.length; i++) {
    if (typeof candidates[i].ctor === 'function') {
      return { ctor: candidates[i].ctor as PlayFunSDKConstructor };
    }
  }

  return null;
};

const waitForSDKScript = async (): Promise<void> => {
  if (typeof document === 'undefined') {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-playfun-sdk]') as HTMLScriptElement | null;

    const done = () => {
      cleanup();
      resolve();
    };
    const fail = () => {
      cleanup();
      reject(new Error('playfun_sdk_load_failed'));
    };

    let timeoutId: number | null = null;
    const cleanup = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };

    if (existing) {
      if ((existing as any).dataset?.loaded === 'true' || getGlobalPlayFunSDK()) {
        done();
        return;
      }
      existing.addEventListener('load', () => {
        existing.setAttribute('data-loaded', 'true');
        done();
      }, { once: true });
      existing.addEventListener('error', fail, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = resolvedPlayFunSdkUrl;
      script.async = true;
      script.setAttribute('data-playfun-sdk', 'true');
      script.addEventListener('load', () => {
        script.setAttribute('data-loaded', 'true');
        done();
      }, { once: true });
      script.addEventListener('error', fail, { once: true });
      document.head.appendChild(script);
    }

    timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('playfun_sdk_load_timeout'));
    }, PLAYFUN_SDK_LOAD_TIMEOUT_MS);
  });
};

const loadPlayFunSDK = async (): Promise<PlayFunSDKLoadResult> => {
  for (let attempt = 1; attempt <= PLAYFUN_SDK_LOAD_RETRIES; attempt++) {
    const fromGlobal = getGlobalPlayFunSDK();
    if (fromGlobal) {
      return fromGlobal;
    }

    try {
      await waitForSDKScript();
      const loaded = getGlobalPlayFunSDK();
      if (loaded) {
        return loaded;
      }
    } catch {
      // retry below
    }

    if (attempt < PLAYFUN_SDK_LOAD_RETRIES) {
      await sleep(250 * attempt);
    }
  }

  throw new Error('playfun_sdk_unavailable');
};

const ensureRefreshPointsAndMultiplier = (sdk: any) => {
  if (!sdk || typeof sdk.refreshPointsAndMultiplier === 'function') {
    return;
  }

  sdk.refreshPointsAndMultiplier = async () => {
    if (typeof sdk.getPoints !== 'function') {
      throw new Error('sdk_getPoints_unavailable');
    }

    const { points, activeMultiplier } = await sdk.getPoints();
    const parsedPoints = Number.parseInt(String(points), 10);
    const currentPointsDisplay = sdk.pointsDisplay || {};
    const nextPointsDisplay = {
      ...currentPointsDisplay,
      points: Number.isFinite(parsedPoints)
        ? parsedPoints
        : (typeof currentPointsDisplay.points === 'number' ? currentPointsDisplay.points : 0),
      activeMultiplier: (typeof activeMultiplier === 'number' && Number.isFinite(activeMultiplier))
        ? activeMultiplier
        : currentPointsDisplay.activeMultiplier,
    };

    if (typeof sdk.updateAndReplicate === 'function') {
      sdk.updateAndReplicate({ pointsDisplay: nextPointsDisplay });
      return;
    }

    if (sdk.state && typeof sdk.state === 'object') {
      sdk.state = {
        ...sdk.state,
        pointsDisplay: nextPointsDisplay,
      };
    }
  };

  sdk.__apolloRefreshPointsAndMultiplierShim = true;
};

const ogpState: {
  sdk: any | null;
  initPromise: Promise<any | null> | null;
} = {
  sdk: null,
  initPromise: null,
};

export const initOpenGameSDK = async (): Promise<any | null> => {
  if (disableClientSDK) {
    return null;
  }
  if (!isEmbeddedInPlayFunPlatform()) {
    return null;
  }
  if (ogpState.sdk) {
    return ogpState.sdk;
  }
  if (ogpState.initPromise) {
    return ogpState.initPromise;
  }

  const { apiKey, gameId, baseUrl } = getConfig();
  if (!apiKey || !gameId) {
    console.warn('[ogp-sdk] Missing config', { hasApiKey: !!apiKey, hasGameId: !!gameId });
    return null;
  }

  ogpState.initPromise = (async () => {
    try {
      const loaded = await loadPlayFunSDK();
      const normalizedBaseUrl = (baseUrl || '').replace(/\/+$/, '');
      const sdk = new loaded.ctor({
        apiKey,
        gameId,
        baseUrl: normalizedBaseUrl || undefined,
        apiUrl: normalizedBaseUrl || undefined,
        apiURL: normalizedBaseUrl || undefined,
        ui: {
          usePointsWidget: defaultUsePointsWidget,
        },
      });

      ensureRefreshPointsAndMultiplier(sdk);
      await sdk.init();

      ogpState.sdk = sdk;
      return sdk;
    } catch (err) {
      ogpState.sdk = null;
      console.warn('[ogp-sdk] init failed', err);
      return null;
    }
  })().finally(() => {
    ogpState.initPromise = null;
  });

  return ogpState.initPromise;
};

export const getOpenGameSDK = () => ogpState.sdk;

type TokenResolveOptions = {
  sdk: any | null;
  timeoutMs: number;
  trySdkLogin: boolean;
  reason: string;
};

const resolveSessionTokenBestEffort = async (opts: TokenResolveOptions): Promise<string | undefined> => {
  const immediate = getImmediateSessionToken(opts.sdk);
  if (immediate) {
    return immediate;
  }

  if (opts.trySdkLogin && opts.sdk && typeof opts.sdk.login === 'function' && hasCompleteSDKConfig()) {
    try {
      await withTimeout(
        Promise.resolve(opts.sdk.login()),
        Math.max(1_000, Math.min(opts.timeoutMs, OGP_LOGIN_TIMEOUT_MS)),
        'ogp_login_timeout'
      );
    } catch {
      // continue with other channels
    }
  }

  const start = Date.now();
  let lastHandshakeAt = 0;
  let onlineRetryCount = 0;
  while (Date.now() - start < opts.timeoutMs) {
    const token = getImmediateSessionToken(opts.sdk);
    if (token) {
      logFetchedIdentifiers(opts.reason, { sessionToken: token });
      return token;
    }

    const now = Date.now();
    if (hasParentFrame() && now - lastHandshakeAt >= OGP_PLAYER_ID_HANDSHAKE_INTERVAL_MS) {
      requestEmbedCredentials('wait_session_token_retry');
      lastHandshakeAt = now;
    }

    if (isOnline()) {
      if (onlineRetryCount >= OGP_SESSION_TOKEN_MAX_RETRIES_ONLINE) {
        break;
      }
      onlineRetryCount += 1;
    }

    const remainingMs = opts.timeoutMs - (Date.now() - start);
    await sleep(Math.min(OGP_SESSION_TOKEN_POLL_INTERVAL_MS, Math.max(50, remainingMs)));
  }

  return getImmediateSessionToken(opts.sdk);
};

let tokenBackgroundPromise: Promise<string | undefined> | null = null;

const ensureSessionTokenBackgroundFetch = (sdk: any | null) => {
  if (!sdk && !hasCompleteSDKConfig()) {
    return;
  }
  if (tokenBackgroundPromise || getImmediateSessionToken(sdk)) {
    return;
  }

  tokenBackgroundPromise = resolveSessionTokenBestEffort({
    sdk,
    timeoutMs: OGP_SESSION_TOKEN_BACKGROUND_MS,
    trySdkLogin: true,
    reason: 'background_session_token',
  }).finally(() => {
    tokenBackgroundPromise = null;
  });
};

export const waitForOGPPlayerId = async (timeoutMs: number = 15_000): Promise<string | undefined> => {
  ensureOGPMessageListener();
  requestEmbedCredentials('wait_start');
  if (hasCompleteSDKConfig()) {
    void initOpenGameSDK().catch(() => undefined);
  }

  const immediate = getImmediatePlayerId();
  if (immediate) {
    logFetchedIdentifiers('client_context_player_id', { playerId: immediate });
    return immediate;
  }

  if (!(timeoutMs > 0)) {
    return undefined;
  }

  const start = Date.now();
  let lastHandshakeAt = Date.now();
  while (Date.now() - start < timeoutMs) {
    const playerId = getImmediatePlayerId();
    if (playerId) {
      logFetchedIdentifiers('wait_player_id', { playerId });
      return playerId;
    }

    const now = Date.now();
    if (hasParentFrame() && now - lastHandshakeAt >= OGP_PLAYER_ID_HANDSHAKE_INTERVAL_MS) {
      requestEmbedCredentials('wait_retry');
      lastHandshakeAt = now;
    }

    const remainingMs = timeoutMs - (Date.now() - start);
    await sleep(Math.min(OGP_PLAYER_ID_POLL_INTERVAL_MS, Math.max(50, remainingMs)));
  }

  return undefined;
};

export const waitForOGPSessionToken = async (timeoutMs: number = 15_000): Promise<string | undefined> => {
  ensureOGPMessageListener();
  requestEmbedCredentials('wait_session_token_start');
  const sdk = hasCompleteSDKConfig()
    ? await initOpenGameSDK().catch(() => null)
    : null;

  const token = await resolveSessionTokenBestEffort({
    sdk,
    timeoutMs,
    trySdkLogin: false,
    reason: 'wait_session_token',
  });

  if (token) {
    return token;
  }

  return undefined;
};

export const getOGPSession = async (opts?: {
  requireLogin?: boolean;
  loginTimeoutMs?: number;
  loginRetries?: number;
}): Promise<OGPSession> => {
  const requireLogin = opts?.requireLogin === true;
  const loginTimeoutMs = opts?.loginTimeoutMs ?? 15_000;
  const loginRetries = Math.max(1, Math.floor(opts?.loginRetries ?? 1));
  const { gameId: configuredGameId } = getConfig();

  let playerId = getImmediatePlayerId();
  let sessionToken = getImmediateSessionToken();

  if (!requireLogin) {
    if (!playerId) {
      playerId = await waitForOGPPlayerId(Math.min(loginTimeoutMs, 3_000));
    }

    if (playerId) {
      if (hasCompleteSDKConfig()) {
        void initOpenGameSDK().catch(() => undefined);
      }
      if (!sessionToken) {
        ensureSessionTokenBackgroundFetch(ogpState.sdk);
      }

      console.error('[OGP debug] Retrieved session identifiers', {
        source: 'player_id_fast_path',
        playerId,
        sessionToken,
      });
      return { sdk: null, playerId, gameId: configuredGameId, sessionToken };
    }
  }

  const sdk = await withTimeout(
    initOpenGameSDK(),
    Math.max(1_000, Math.min(loginTimeoutMs, 10_000)),
    'ogp_session_init_timeout'
  ).catch(() => null);

  if (sdk) {
    playerId = playerId || getPlayerIdFromSDK(sdk);
    sessionToken = sessionToken || getSessionTokenFromSDK(sdk);

    if (requireLogin && !playerId && typeof sdk.login === 'function') {
      for (let attempt = 1; attempt <= loginRetries; attempt++) {
        try {
          await withTimeout(
            Promise.resolve(sdk.login()),
            Math.max(1_000, Math.min(loginTimeoutMs, OGP_LOGIN_TIMEOUT_MS)),
            'ogp_login_timeout'
          );
        } catch {
          // continue
        }

        playerId = playerId || getPlayerIdFromSDK(sdk) || getImmediatePlayerId(sdk);
        if (playerId) {
          break;
        }
      }
    }

    if (!playerId) {
      playerId = await waitForOGPPlayerId(Math.min(loginTimeoutMs, 3_000));
    }

    if (!sessionToken && requireLogin) {
      // Score submission should not wait indefinitely on token.
      sessionToken = await resolveSessionTokenBestEffort({
        sdk,
        timeoutMs: OGP_SESSION_TOKEN_BLOCKING_MS,
        trySdkLogin: true,
        reason: 'blocking_session_token',
      });
    }

    if (!sessionToken) {
      // Continue improving auth data in background without blocking gameplay or submission.
      ensureSessionTokenBackgroundFetch(sdk);
    }

    console.error('[OGP debug] Retrieved session identifiers', {
      source: 'sdk',
      playerId,
      sessionToken,
    });

    return { sdk, playerId, gameId: sdk.gameId || configuredGameId, sessionToken };
  }

  if (!playerId) {
    playerId = await waitForOGPPlayerId(Math.min(loginTimeoutMs, 3_000));
  }

  if (!sessionToken && requireLogin) {
    sessionToken = await resolveSessionTokenBestEffort({
      sdk: null,
      timeoutMs: OGP_SESSION_TOKEN_BLOCKING_MS,
      trySdkLogin: false,
      reason: 'blocking_session_token_no_sdk',
    });
  }

  if (!sessionToken) {
    ensureSessionTokenBackgroundFetch(null);
  }

  console.error('[OGP debug] Retrieved session identifiers', {
    source: 'no_sdk',
    playerId,
    sessionToken,
  });

  return { sdk: null, playerId, gameId: configuredGameId, sessionToken };
};

export const getScoreValidationBaseUrl = (): string | null => {
  const { scoreApiBaseUrl } = getConfig();
  if (!scoreApiBaseUrl) {
    return null;
  }
  return scoreApiBaseUrl.replace(/\/+$/, '');
};

export const submitScoreValidation = async (
  payload: ScoreValidationRequest
): Promise<ScoreValidationResponse | null> => {
  const baseUrl = getScoreValidationBaseUrl();
  if (!baseUrl) {
    console.warn('[score-validation] Missing score API base URL.');
    return null;
  }

  const res = await fetch(`${baseUrl}${SCORE_VALIDATION_ROUTE}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let data: ScoreValidationResponse | null = null;
  try {
    data = (await res.json()) as ScoreValidationResponse;
  } catch (err) {
    console.warn('[score-validation] Failed to parse response JSON', err);
  }

  if (!res.ok) {
    console.warn('[score-validation] Request failed', { status: res.status, data });
  }

  return data;
};
